import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

import {
    Drawer,
    Box,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    TextField,
    ListItemText,
    Checkbox,
    InputAdornment,
    FormControlLabel,
    Switch,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import CalendarToday from '@mui/icons-material/CalendarToday';

import { SCUIButton } from '../../../atomic/atoms';
import { cargoCompanyShipmentStates } from '../helper/enums';
import { LostCargoCommand } from '../../../services/command';
import { LOSTCARGO } from '../lostCargo.classes';
import {
    MAX_SHIPMENT_NUMBERS,
    parseShipmentNumbers,
    resolveNotFoundShipmentNumbers,
    sortByLostPackageDateAsc,
    buildLostPackageShipmentsRequest,
    getCalendarDaysBetween,
    getLostCargoEndDateMin,
    getLostCargoStartDateMax,
    getMaxSelectableDate,
    isLostCargoStartBeforeEnd,
    MAX_LOST_CARGO_DATE_RANGE_DAYS,
    parseApiDateString,
} from '../LostCargo.helper';

const defaultFormValues = {
    customerShipmentNumbers: '',
    customerInvoiceNumber: '',
    customerCargoCompanyId: [],
    cargoCompanyShipmentNumbers: '',
    cargoCompanyInvoiceNumber: '',
    cargoCompanyShipmentState: [],
    startDate: '',
    endDate: '',
    onlyTwentyOneDaysOrMoreLostPackages: false,
};

const formatApiDateDisplay = (apiDate: string): string => {
    if (!apiDate) {
        return '';
    }
    const [year, month, day] = apiDate.split('-');
    if (!year || !month || !day) {
        return '';
    }
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
};

const openNativeDatePicker = (input: HTMLInputElement | null) => {
    if (!input) {
        return;
    }
    if (typeof input.showPicker === 'function') {
        input.showPicker();
    } else {
        input.click();
    }
};

const LostCargoDateField = ({
    className,
    label,
    value,
    disabled,
    min,
    max,
    onChange,
}: {
    className: string;
    label: string;
    value: string;
    disabled?: boolean;
    min?: string;
    max?: string;
    onChange: (value: string) => void;
}) => {
    const dateInputRef = useRef<HTMLInputElement>(null);

    return (
        <Box sx={{ position: 'relative', flex: 1 }}>
            <TextField
                className={className}
                label={label}
                fullWidth
                margin="normal"
                value={formatApiDateDisplay(value)}
                disabled={disabled}
                placeholder="GG/AA/YYYY"
                onClick={() => !disabled && openNativeDatePicker(dateInputRef.current)}
                slotProps={{
                    inputLabel: { shrink: true },
                    input: {
                        readOnly: true,
                        sx: { cursor: disabled ? 'default' : 'pointer' },
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    edge="end"
                                    disabled={disabled}
                                    aria-label={label}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openNativeDatePicker(dateInputRef.current);
                                    }}
                                >
                                    <CalendarToday fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <input
                ref={dateInputRef}
                type="date"
                value={value || ''}
                min={min || undefined}
                max={max || undefined}
                disabled={disabled}
                onChange={(e) => onChange(e.target.value)}
                tabIndex={-1}
                aria-hidden
                style={{
                    position: 'absolute',
                    opacity: 0,
                    width: 0,
                    height: 0,
                    pointerEvents: 'none',
                }}
            />
        </Box>
    );
};

export const LostCargoSearchDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    setGridData,
    setNotFoundShipments,
    setTotalCount,
    cargoCompanies,
    setIsLoading,
    setIsSearched,
    triggerSearch,
    setTriggerSearch,
    pageSize = 10,
    pageNumber = 0,
    setPageNumber,
    setSelectedRows,
}: any) => {
    const { t } = useTranslation();

    const { register, handleSubmit, setValue, watch, getValues, reset } = useForm<any>({
        defaultValues: defaultFormValues,
    });

    const cargoCompanyShipmentNumbersRaw = watch('cargoCompanyShipmentNumbers');
    const customerShipmentNumbersRaw = watch('customerShipmentNumbers');
    const startDate = watch('startDate');
    const endDate = watch('endDate');
    const maxSelectableDate = getMaxSelectableDate();
    const startDateMax = getLostCargoStartDateMax(endDate);
    const endDateMin = getLostCargoEndDateMin(startDate);

    const cargoShipmentNumbers = parseShipmentNumbers(cargoCompanyShipmentNumbersRaw || '');
    const customerShipmentNumbers = parseShipmentNumbers(customerShipmentNumbersRaw || '');
    const hasShipmentNumberSearch = cargoShipmentNumbers.length > 0 || customerShipmentNumbers.length > 0;

    const RequiredMark = () =>
        hasShipmentNumberSearch ? null : (
            <p className={LOSTCARGO.searchDrawerRequiredHint} style={{ fontSize: '14px', flexShrink: 0 }}>
                {'(*)'}
            </p>
        );

    const isStandardFilterSetFilled = (values: {
        startDate: string;
        endDate: string;
        customerCargoCompanyId: number[];
        cargoCompanyShipmentState: number[];
    }) =>
        Boolean(values.startDate) &&
        Boolean(values.endDate) &&
        values.customerCargoCompanyId.length > 0 &&
        values.cargoCompanyShipmentState.length > 0;

    const handleClearAndClose = () => {
        handleDrawerClose();
    };

    const clearForm = () => {
        reset({ ...defaultFormValues });
        setValue('startDate', '');
        setValue('endDate', '');
    };

    const handleFilter = () => {
        if (!checkDateDiffValid(getValues('startDate'), getValues('endDate'))) {
            return;
        }

        const totalNumbers = cargoShipmentNumbers.length + customerShipmentNumbers.length;
        if (totalNumbers > MAX_SHIPMENT_NUMBERS) {
            Swal.fire(t('lostCargo.maxShipmentNumbers', { max: MAX_SHIPMENT_NUMBERS }), '', 'error');
            return;
        }

        if (
            !hasShipmentNumberSearch &&
            !isStandardFilterSetFilled({
                startDate: getValues('startDate'),
                endDate: getValues('endDate'),
                customerCargoCompanyId: getValues('customerCargoCompanyId') || [],
                cargoCompanyShipmentState: getValues('cargoCompanyShipmentState') || [],
            })
        ) {
            Swal.fire(t('lostCargo.fillRequired'), '', 'error');
            return;
        }

        setIsLoading(true);
        setSelectedRows?.([]);
        setPageNumber?.(0);
        getGridData({ pageNumber: 0 });
        handleClearAndClose();
    };

    const checkDateDiffValid = (start: string, end: string) => {
        if (!start || !end) {
            return true;
        }

        if (!isLostCargoStartBeforeEnd(start, end)) {
            Swal.fire(t('lostCargo.dateEndStartError'), '', 'error');
            return false;
        }

        const parsedStart = parseApiDateString(start);
        const parsedEnd = parseApiDateString(end);
        if (
            parsedStart &&
            parsedEnd &&
            getCalendarDaysBetween(parsedStart, parsedEnd) > MAX_LOST_CARGO_DATE_RANGE_DAYS
        ) {
            Swal.fire(t('lostCargo.dateGapError'), '', 'error');
            return false;
        }

        return true;
    };

    const handleStartDateChange = (value: string) => {
        const currentEnd = getValues('endDate');
        if (value && currentEnd && !isLostCargoStartBeforeEnd(value, currentEnd)) {
            Swal.fire(t('lostCargo.dateEndStartError'), '', 'error');
            return;
        }
        setValue('startDate', value);
    };

    const handleEndDateChange = (value: string) => {
        const currentStart = getValues('startDate');
        if (value && currentStart && !isLostCargoStartBeforeEnd(currentStart, value)) {
            Swal.fire(t('lostCargo.dateEndStartError'), '', 'error');
            return;
        }
        setValue('endDate', value);
    };

    const getGridData = (overrides?: { pageNumber?: number; pageSize?: number }) => {
        if (!checkDateDiffValid(getValues('startDate'), getValues('endDate'))) {
            setIsLoading(false);
            setTriggerSearch(false);
            return;
        }

        setIsLoading(true);

        const effectivePageNumber = overrides?.pageNumber ?? pageNumber ?? 0;
        const effectivePageSize = overrides?.pageSize ?? pageSize ?? 10;

        const parsedCargoNumbers = parseShipmentNumbers(getValues('cargoCompanyShipmentNumbers') || '');
        const parsedCustomerNumbers = parseShipmentNumbers(getValues('customerShipmentNumbers') || '');
        const hasShipmentNumberSearchFromValues = parsedCargoNumbers.length > 0 || parsedCustomerNumbers.length > 0;

        if (
            !hasShipmentNumberSearchFromValues &&
            !isStandardFilterSetFilled({
                startDate: getValues('startDate'),
                endDate: getValues('endDate'),
                customerCargoCompanyId: getValues('customerCargoCompanyId') || [],
                cargoCompanyShipmentState: getValues('cargoCompanyShipmentState') || [],
            })
        ) {
            setIsLoading(false);
            setTriggerSearch(false);
            return;
        }

        const params = buildLostPackageShipmentsRequest({
            customerInvoiceNumber: getValues('customerInvoiceNumber'),
            cargoCompanyInvoiceNumber: getValues('cargoCompanyInvoiceNumber'),
            customerShipmentNumbers: parsedCustomerNumbers,
            cargoCompanyShipmentNumbers: parsedCargoNumbers,
            startDate: getValues('startDate'),
            endDate: getValues('endDate'),
            customerCargoCompanyId: getValues('customerCargoCompanyId'),
            cargoCompanyShipmentState: getValues('cargoCompanyShipmentState'),
            onlyTwentyOneDaysOrMoreLostPackages: getValues('onlyTwentyOneDaysOrMoreLostPackages'),
            take: hasShipmentNumberSearchFromValues
                ? Math.min(Math.max(parsedCargoNumbers.length + parsedCustomerNumbers.length, 1), MAX_SHIPMENT_NUMBERS)
                : effectivePageSize,
            skip: hasShipmentNumberSearchFromValues ? 0 : effectivePageNumber * effectivePageSize,
        });

        LostCargoCommand.GetLostCargoList(params)
            .then((res) => {
                if (res) {
                    setIsSearched(true);
                    let shipments = res.data?.lostPackageShipments || [];
                    shipments = sortByLostPackageDateAsc(shipments);

                    const notFound = hasShipmentNumberSearchFromValues
                        ? resolveNotFoundShipmentNumbers(parsedCargoNumbers, parsedCustomerNumbers, shipments)
                        : [];

                    setNotFoundShipments?.(notFound);
                    setGridData(shipments);
                    setTotalCount?.(typeof res.data?.totalCount === 'number' ? res.data.totalCount : shipments.length);

                    if (shipments.length === 0 && notFound.length === 0) {
                        Swal.fire({
                            position: 'center',
                            icon: 'error',
                            title: t('general.noDataFound'),
                            showConfirmButton: false,
                            timer: 1500,
                        });
                    }
                }
                setIsLoading(false);
                setTriggerSearch(false);
            })
            .catch(() => {
                Swal.fire(t('general.noDataFound'), '', 'error');
                setNotFoundShipments?.([]);
                setTotalCount?.(0);
                setIsLoading(false);
                setTriggerSearch(false);
            });
    };

    useEffect(() => {
        if (triggerSearch) {
            getGridData();
        }
    }, [triggerSearch, pageNumber, pageSize]);

    return (
        <Drawer
            variant="temporary"
            anchor="right"
            open={isDrawerOpen}
            onClose={handleClearAndClose}
            slotProps={{
                paper: {
                    className: LOSTCARGO.searchDrawerPaper,
                    sx: {
                        padding: '16px',
                        width: 360,
                    },
                },
            }}
        >
            <Box className={LOSTCARGO.searchDrawerBody} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    className={LOSTCARGO.searchDrawerHeader}
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <p className={LOSTCARGO.searchDrawerTitle} style={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
                        {t('lostCargo.detailedSearch')}
                    </p>
                    <IconButton
                        className={LOSTCARGO.inputIconSearchDrawerClose}
                        sx={{
                            marginLeft: 'auto',
                            '& .MuiSvgIcon-root': {
                                width: '2rem',
                                height: '2rem',
                                color: '#000',
                            },
                        }}
                        onClick={handleClearAndClose}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <Divider sx={{ width: '100%', margin: '4px 0 24px 0' }} />
                <form onSubmit={handleSubmit(handleFilter)}>
                    <Box
                        className={LOSTCARGO.searchDrawerForm}
                        sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    >
                        <Box
                            className={LOSTCARGO.searchDrawerDateRow}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                                width: '100%',
                            }}
                        >
                            <Box
                                className={LOSTCARGO.searchDrawerDateRangeWrap}
                                sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <LostCargoDateField
                                        className={LOSTCARGO.inputDateSearchStart}
                                        label={t('general.startDate')}
                                        value={startDate || ''}
                                        max={startDateMax}
                                        onChange={handleStartDateChange}
                                    />
                                </Box>
                                <RequiredMark />
                            </Box>
                            <Box
                                className={LOSTCARGO.searchDrawerDateRangeWrap}
                                sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <LostCargoDateField
                                        className={LOSTCARGO.inputDateSearchEnd}
                                        label={t('general.endDate')}
                                        value={endDate || ''}
                                        min={endDateMin}
                                        max={maxSelectableDate}
                                        onChange={handleEndDateChange}
                                    />
                                </Box>
                                <RequiredMark />
                            </Box>
                        </Box>
                        <Box className={LOSTCARGO.searchDrawerOnlyTwentyOneDaysRow} sx={{ marginTop: '4px' }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        className={LOSTCARGO.inputSwitchOnlyTwentyOneDays}
                                        checked={Boolean(watch('onlyTwentyOneDaysOrMoreLostPackages'))}
                                        onChange={(e) =>
                                            setValue('onlyTwentyOneDaysOrMoreLostPackages', e.target.checked)
                                        }
                                        size="small"
                                    />
                                }
                                label={
                                    <span style={{ fontSize: '14px' }}>
                                        {t('lostCargo.onlyTwentyOneDaysOrMoreLostPackages')}
                                    </span>
                                }
                            />
                        </Box>
                        <FormControl
                            className={LOSTCARGO.searchDrawerCargoCompanyField}
                            fullWidth
                            margin="normal"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <InputLabel shrink sx={{ fontSize: '14px' }}>
                                    {t('lostCargo.cargoCompany')}
                                </InputLabel>
                                <Select
                                    className={LOSTCARGO.inputSelectSearchCargoCompanies}
                                    value={watch('customerCargoCompanyId')}
                                    multiple
                                    onChange={(e) => setValue('customerCargoCompanyId', e.target.value)}
                                    renderValue={(selected: any) =>
                                        (cargoCompanies || [])
                                            .filter((c: any) => selected.includes(c.id))
                                            .map((c: any) => c.name)
                                            .join(', ')
                                    }
                                    sx={{
                                        maxHeight: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: '14px',
                                        width: '95%',
                                    }}
                                >
                                    {(cargoCompanies || []).map((option: any) => (
                                        <MenuItem key={option.id} value={option.id}>
                                            <Checkbox checked={watch('customerCargoCompanyId')?.includes(option.id)} />
                                            <ListItemText primary={option.name} />
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                            <RequiredMark />
                        </FormControl>
                        <FormControl
                            className={LOSTCARGO.searchDrawerShipmentStatusField}
                            fullWidth
                            margin="normal"
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: '100%',
                            }}
                        >
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                <InputLabel shrink sx={{ fontSize: '14px' }}>
                                    {t('lostCargo.shipmentStatus')}
                                </InputLabel>
                                <Select
                                    className={LOSTCARGO.inputSelectSearchShipmentStatus}
                                    value={watch('cargoCompanyShipmentState')}
                                    multiple
                                    onChange={(e) => setValue('cargoCompanyShipmentState', e.target.value)}
                                    renderValue={(selected: any) =>
                                        cargoCompanyShipmentStates
                                            .filter((s: any) => selected.includes(s.value))
                                            .map((s: any) => t(s.label))
                                            .join(', ')
                                    }
                                    sx={{
                                        maxHeight: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: '14px',
                                        width: '95%',
                                    }}
                                >
                                    {cargoCompanyShipmentStates
                                        .filter((state: any) => [2, 20, 21].includes(state.value))
                                        .map((option: any) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                <Checkbox
                                                    checked={watch('cargoCompanyShipmentState')?.includes(option.value)}
                                                />
                                                <ListItemText primary={t(option.label)} />
                                            </MenuItem>
                                        ))}
                                </Select>
                            </Box>
                            <RequiredMark />
                        </FormControl>
                        <TextField
                            className={LOSTCARGO.inputTextCargoInvoiceNo}
                            label={t('lostCargo.cargoInvoiceNo')}
                            fullWidth
                            margin="normal"
                            {...register('cargoCompanyInvoiceNumber')}
                        />
                        <TextField
                            className={LOSTCARGO.inputTextCustomerInvoiceNo}
                            label={t('lostCargo.customerInvoiceNo')}
                            fullWidth
                            margin="normal"
                            {...register('customerInvoiceNumber')}
                        />
                        <TextField
                            className={LOSTCARGO.inputTextCargoShipmentNo}
                            label={t('lostCargo.cargoShipmentNo')}
                            fullWidth
                            margin="normal"
                            multiline
                            minRows={4}
                            maxRows={12}
                            helperText={t('lostCargo.shipmentNumbersPasteHint', { max: MAX_SHIPMENT_NUMBERS })}
                            {...register('cargoCompanyShipmentNumbers')}
                        />
                        <TextField
                            className={LOSTCARGO.inputTextCustomerShipmentNo}
                            label={t('lostCargo.customerShipmentNo')}
                            fullWidth
                            margin="normal"
                            multiline
                            minRows={4}
                            maxRows={12}
                            helperText={t('lostCargo.shipmentNumbersPasteHint', { max: MAX_SHIPMENT_NUMBERS })}
                            {...register('customerShipmentNumbers')}
                        />

                        <Box
                            className={LOSTCARGO.searchDrawerFooter}
                            sx={{ display: 'flex', flexDirection: 'row', gap: '24px', marginTop: '16px' }}
                        >
                            <SCUIButton
                                className={LOSTCARGO.inputButtonSearchClear}
                                text={t('general.clear')}
                                onClick={clearForm}
                                type="primary"
                                buttonType="button"
                                sizeType="fixed"
                                style={{ backgroundColor: 'red', marginRight: '8px', borderColor: 'red' }}
                                size={'m'}
                                fullwidth
                            />
                            <SCUIButton
                                className={LOSTCARGO.inputButtonSearchFilter}
                                text={t('general.filter')}
                                type="primary"
                                sizeType="fixed"
                                size={'m'}
                                fullwidth
                            />
                        </Box>
                    </Box>
                </form>
            </Box>
        </Drawer>
    );
};
